/**
 * Content Service - Reads content data from Hostinger /content/ folder using index.json
 * This service fetches content data directly from the server using a master index
 */

import { CacheManager } from '../utils/cacheManager';
import { normalizeArrayData } from '../utils/dataFormatHandler';
import safeStorage from '../utils/safeStorage';
import { STORAGE_KEYS } from '../utils/storageKeys';

class ContentService {
  constructor() {
    this.baseUrl = window.location.origin;
    this.cacheManager = new CacheManager(5 * 60 * 1000); // 5 minutes
  }

  /**
   * Get all content from the master /content/index.json file
   * @returns {Promise<Array>} Array of content objects
   */
  async getContent() {
    // Check cache first
    const cachedContent = this.cacheManager.get();
    if (cachedContent) {
      return cachedContent;
    }

    try {
      console.log('🌐 Fetching content from master index...');
      
      const response = await fetch(`${this.baseUrl}/content/index.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Content loaded from master index:', data);
      
      // Handle both array format and object format using shared utility
      const content = normalizeArrayData(data, 'content', 'content').map(item => ({
        ...item,
        postedAt: item.postedAt || item.createdAt || item.updatedAt || null
      }));
      
      console.log('📊 Content data processed:', {
        contentCount: content.length,
        content: content.map(c => ({ id: c.id, name: c.name, folder: c.folder }))
      });
      
      // Cache the results
      this.cacheManager.set(content);
      
      return content;
    } catch (error) {
      console.error('❌ Error fetching content from index:', error);
      
      // Fallback to localStorage if server fetch fails
      const fallbackContent = this.getContentFromLocalStorage().map(item => ({
        ...item,
        postedAt: item.postedAt || item.createdAt || item.updatedAt || null
      }));
      if (fallbackContent.length > 0) {
        console.log('📱 Using localStorage fallback for content');
        return fallbackContent;
      }
      
      return [];
    }
  }

  /**
   * Get a specific content item by ID from its individual content.json
   * @param {string} contentId - The content ID (folder name)
   * @returns {Promise<Object|null>} Content data or null if not found
   */
  async getContentItem(contentId) {
    try {
      console.log(`🔍 Fetching content data for: ${contentId}`);
      
      const response = await fetch(`${this.baseUrl}/content/${contentId}/content.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentData = await response.json();
      console.log('✅ Content data loaded:', contentData);
      
      return contentData;
    } catch (error) {
      console.error(`❌ Error fetching content ${contentId}:`, error);
      return null;
    }
  }

  /**
   * Get content from localStorage (fallback)
   */
  getContentFromLocalStorage() {
    return safeStorage.getJSON(STORAGE_KEYS.CONTENT || 'rainbow_sails_content', []);
  }

  /**
   * Save content to localStorage (for offline access)
   */
  saveContentToLocalStorage(content) {
    if (safeStorage.setJSON(STORAGE_KEYS.CONTENT || 'rainbow_sails_content', content)) {
      console.log('💾 Content saved to localStorage');
    }
  }

  /**
   * Clear cache (useful for testing or when you know data has changed)
   */
  clearCache() {
    this.cacheManager.clear();
    console.log('🗑️ Content cache cleared');
  }
}

// Export a singleton instance
const contentServiceInstance = new ContentService();
export default contentServiceInstance;

